/** Mutually exclusive home product surface (not weekday/holiday learning mode). */
export type ProductStudyMode = 'review' | 'offscreen';

/** Off-screen interaction lane (Charter P2). */
export type OffscreenInteractionMode = 'broadcast' | 'light_offscreen' | 'dialogue';

export type OffscreenPrefs = {
  interactionMode: OffscreenInteractionMode;
  /** Prefer glance when content asks; user can force none later */
  allowGlance: boolean;
  /** Optional keliagent Pi model id; empty uses server default. */
  osrModel: string;
  generationGroups: number | "all";
};

export const PRODUCT_STUDY_MODE_STORAGE_KEY = 'osr_product_study_mode';
export const OFFSCREEN_PREFS_STORAGE_KEY = 'osr_offscreen_prefs_v1';

let memoryMode: ProductStudyMode = 'review';
let memoryOffscreenPrefs: OffscreenPrefs = {
  interactionMode: 'broadcast',
  allowGlance: true,
  osrModel: '',
  generationGroups: 1,
};

export function isProductStudyMode(value: unknown): value is ProductStudyMode {
  return value === 'review' || value === 'offscreen';
}

export function isOffscreenInteractionMode(value: unknown): value is OffscreenInteractionMode {
  return value === 'broadcast' || value === 'light_offscreen' || value === 'dialogue';
}

export function getMemoryProductStudyMode(): ProductStudyMode {
  return memoryMode;
}

export function setMemoryProductStudyMode(mode: ProductStudyMode): void {
  memoryMode = mode;
}

export function getMemoryOffscreenPrefs(): OffscreenPrefs {
  return memoryOffscreenPrefs;
}

export function setMemoryOffscreenPrefs(prefs: OffscreenPrefs): void {
  memoryOffscreenPrefs = prefs;
}

function readLocal(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocal(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Session memory still tracks the mode for this run.
  }
}

export async function loadPersistedProductStudyMode(): Promise<ProductStudyMode> {
  const saved = readLocal(PRODUCT_STUDY_MODE_STORAGE_KEY);
  if (isProductStudyMode(saved)) {
    memoryMode = saved;
    return saved;
  }
  return memoryMode;
}

export async function persistProductStudyMode(mode: ProductStudyMode): Promise<void> {
  memoryMode = mode;
  writeLocal(PRODUCT_STUDY_MODE_STORAGE_KEY, mode);
}

export async function loadPersistedOffscreenPrefs(): Promise<OffscreenPrefs> {
  const raw = readLocal(OFFSCREEN_PREFS_STORAGE_KEY);
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as Partial<OffscreenPrefs>;
      memoryOffscreenPrefs = {
        interactionMode: isOffscreenInteractionMode(parsed.interactionMode)
          ? parsed.interactionMode
          : 'broadcast',
        allowGlance: parsed.allowGlance !== false,
        osrModel: typeof parsed.osrModel === 'string' ? parsed.osrModel.trim() : '',
        generationGroups: parsed.generationGroups === 'all' ? 'all' : Math.min(100, Math.max(1, Math.trunc(Number(parsed.generationGroups) || 1))),
      };
    } catch {
      // keep memory default
    }
  }
  return memoryOffscreenPrefs;
}

export async function persistOffscreenPrefs(prefs: OffscreenPrefs): Promise<void> {
  memoryOffscreenPrefs = prefs;
  writeLocal(OFFSCREEN_PREFS_STORAGE_KEY, JSON.stringify(prefs));
}
